import { gql } from '@apollo/client';
import { NEW_ORDER } from '../../fragments/orders';

export const GET_SELECTORS_DATA = gql`
  query fetchDataBase {
    concessions {
      id
      name
      location
      centerLatitud
      centerLongitud
      roadIDs
    }
    interlocutorGroups {
      id
      name
    }
    mediaOfCommunications {
      id
      name
    }
    roads {
      id
      name
      directions
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
      concessionIDs
    }
    teams {
      id
      name
      concessionIDs
      roadIDs
      userIDs
    }
    classifications {
      id
      name
      classificationCode
    }
    predefinedOrders {
      id
      name
    }
  }
`;

/**
 * Query para obtener todas las concesiones (ya filtradas por tenant en backend)
 * El backend automáticamente filtra según availableConcessionIds del AppConfig
 */
export const GET_CONCESSIONS = gql`
  query getConcessions {
    concessions {
      id
      name
      location
      centerLatitud
      centerLongitud
      roadIDs
    }
  }
`;

export const FETCH_ORDERS = gql`
  query FetchOrders($input: FilterOrderList!) {
    orders(input: $input) {
      orders { ...NewOrder }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${NEW_ORDER}
`;

export const GET_ORDER_BY_ID = gql`
  query GetOrderById($id: ID!) {
    order(id: $id) { ...NewOrder }
  }
  ${NEW_ORDER}
`;

// Cross-link 3 (2026-09-22): recorridos vinculados a una OT genérica/campaña.
// Query PROPIA del detalle — nunca meter `linkedSurveillanceRoutes` en
// `NewOrder`: en un listado sería un fan-out por fila (misma regla que viewer*).
export const GET_ORDER_LINKED_ROUTES = gql`
  query GetOrderLinkedRoutes($id: ID!) {
    order(id: $id) {
      id
      linkedSurveillanceRoutes {
        id
        orderId
        roid
        name
        state
        linkedAt
        workOrderIds
      }
    }
  }
`;

// Spec § 16: trabajos de la OT hechos, sin duda, por una patrulla cuyo
// recorrido NO está ligado (etiqueta de aviso de la card del trabajo). Query
// propia y aparte de la de las pills: si su cálculo falla, las pills siguen.
export const GET_ORDER_UNLINKED_WORK_ROUTES = gql`
  query GetOrderUnlinkedWorkRoutes($id: ID!) {
    order(id: $id) {
      id
      unlinkedWorkRoutes {
        workOrderId
        routeId
        routeOrderId
        roid
      }
    }
  }
`;

// Query mínima usada por el loader del recorrido de vigilancia
// (RouteDetailed/loader.ts) — shape ad-hoc, no usa fragment NewOrder a propósito
// porque solo necesita 5 campos para el listado de cross-links.
export const GET_ORDERS_BY_IDS_SERVER = gql`
  query GetOrdersByIdsServer($ids: [ID!]!) {
    ordersByIds(ids: $ids) {
      id
      roid
      state
      type
      description
      createdAt: creationDate
    }
  }
`;

// Modal «Vincular OT» del recorrido (cross-link 3, 2026-09-22): las mismas
// Orders por id que GET_ORDERS_BY_IDS_SERVER más los PK, para que la fila
// enseñe DÓNDE está la OT. Operación propia para no tocar la forma que
// esperan los mocks E2E de GetOrdersByIdsServer.
export const GET_ORDERS_FOR_ROUTE_LINK = gql`
  query GetOrdersForRouteLink($ids: [ID!]!) {
    ordersByIds(ids: $ids) {
      id
      roid
      state
      type
      description
      createdAt: creationDate
      # La fecha que se pinta y por la que se ordena (2026-10-26): la misma
      # por la que filtra «Fecha inicio» en el servidor.
      registerInitDate
      # Columna «Fecha final» (2026-10-28): la misma que el listado de Órdenes
      # de trabajo. Va por DataLoader en el back, sin coste por fila.
      derivedEndDate
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
  }
`;

// Warm-up de caché para los selectors de tabla: resuelve N Orders en UNA
// query servidor (backend `ordersByIds`, tenant-scoped, máx 200 ids, orden de
// salida NO garantizado — irrelevante: solo interesa poblar la caché
// normalizada). Usa EXACTAMENTE el fragment NewOrder (el mismo de
// GET_ORDER_BY_ID) para que los `client.query(GET_ORDER_BY_ID, cache-first)`
// per-row den hit vía el redirect `Query.order → toReference` de policies.ts.
// ⚠️ NO confundir con GET_ORDERS_BY_IDS de abajo (readOrders @client).
export const ORDERS_BY_IDS_WARMUP = gql`
  query OrdersByIds($ids: [ID!]!) {
    ordersByIds(ids: $ids) { ...NewOrder }
  }
  ${NEW_ORDER}
`;

export const GET_ORDERS_BY_IDS = gql`
  query GetOrdersByIds($ids: [ID!]!) {
    readOrders(ids: $ids) @client {
      id
      roid
      roid
      indexExpedition
      indexType
      type
      state
      communicationIDs
      genericOrdersIDs
      campaignIDs
      userId
      registeredByUserId
      classificationIDs
      capitolIDs
      subCapitolIDs
      elementOrderIDs
      parentExpeditionOrderIDs
      childExpeditionOrderIDs
      creationDate
      registerInitDate
      registerEndDate
      description
      notes
      accidentIDs
      actionIDs
      imageIDs
      isTransferred
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
      concessionIDs
      roadIDs
      teamIDs
      predefinedOrderIDs
    }
  }
`;
